import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function KinGovernance({ kinId }) {
  const { user } = useAuth();
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPropose, setShowPropose] = useState(false);
  const [expandedVote, setExpandedVote] = useState(null);
  const [voteDetails, setVoteDetails] = useState(null);
  const [newVote, setNewVote] = useState({
    title: '',
    description: '',
    options: ['', ''],
    expiresInHours: 72
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchVotes();
  }, [kinId]);

  const fetchVotes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/kin-votes/${kinId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVotes(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchVoteDetails = async (voteId) => {
    try {
      const res = await fetch(`/api/kin-votes/vote/${voteId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVoteDetails(data);
      }
    } catch (err) {
      console.error('Failed to fetch vote details:', err);
    }
  };

  const handleVote = async (voteId, optionId) => {
    try {
      const res = await fetch(`/api/kin-votes/vote/${voteId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ optionId })
      });
      if (res.ok) {
        await fetchVotes();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to vote');
      }
    } catch (err) {
      alert('Error casting vote');
    }
  };

  const proposeVote = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`/api/kin-votes/${kinId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: newVote.title,
          description: newVote.description,
          options: newVote.options.filter(opt => opt.trim()),
          expiresInHours: newVote.expiresInHours
        })
      });
      if (res.ok) {
        await fetchVotes();
        setShowPropose(false);
        setNewVote({ title: '', description: '', options: ['', ''], expiresInHours: 72 });
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to propose vote');
      }
    } catch (err) {
      alert('Error proposing vote');
    } finally {
      setSubmitting(false);
    }
  };

  const addOption = () => {
    setNewVote(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const removeOption = (index) => {
    setNewVote(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const updateOption = (index, value) => {
    setNewVote(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      passed: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <div className="text-center py-8">Loading governance...</div>;
  if (error) return <div className="text-center py-8 text-red-600">Error: {error}</div>;

  const activeVotes = votes.filter(v => v.status === 'active');
  const closedVotes = votes.filter(v => v.status !== 'active');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">⚖️ Governance</h2>
        <button
          onClick={() => setShowPropose(!showPropose)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          + Propose Vote
        </button>
      </div>

      {/* Propose Vote Form */}
      {showPropose && (
        <div className="bg-white rounded-lg shadow p-6 border border-blue-200">
          <h3 className="text-lg font-semibold mb-4">Propose a Vote</h3>
          <form onSubmit={proposeVote}>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Title *</label>
              <input
                type="text"
                value={newVote.title}
                onChange={(e) => setNewVote({ ...newVote, title: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={newVote.description}
                onChange={(e) => setNewVote({ ...newVote, description: e.target.value })}
                rows="2"
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Options *</label>
              {newVote.options.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => updateOption(idx, e.target.value)}
                    placeholder={`Option ${idx + 1}`}
                    className="flex-1 border rounded px-3 py-2"
                    required={idx < 2}
                  />
                  {idx > 1 && (
                    <button
                      type="button"
                      onClick={() => removeOption(idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addOption}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                + Add option
              </button>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Expires in (hours)</label>
              <input
                type="number"
                min="1"
                max="720"
                value={newVote.expiresInHours}
                onChange={(e) => setNewVote({ ...newVote, expiresInHours: parseInt(e.target.value) })}
                className="w-32 border rounded px-3 py-2"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400"
              >
                {submitting ? 'Submitting...' : 'Submit Vote'}
              </button>
              <button
                type="button"
                onClick={() => setShowPropose(false)}
                className="border px-4 py-2 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Active Votes */}
      {activeVotes.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Active Votes</h3>
          <div className="space-y-4">
            {activeVotes.map(vote => (
              <div key={vote._id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-lg">{vote.title}</h4>
                    <p className="text-sm text-gray-500">{vote.description}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Proposed by {vote.proposedBy?.name} • {new Date(vote.expiresAt).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadge(vote.status)}`}>
                    {vote.status}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {vote.options.map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => handleVote(vote._id, opt.id)}
                      className="border rounded px-4 py-2 text-sm hover:bg-gray-50 transition"
                    >
                      {opt.text}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => {
                    if (expandedVote === vote._id) {
                      setExpandedVote(null);
                      setVoteDetails(null);
                    } else {
                      setExpandedVote(vote._id);
                      fetchVoteDetails(vote._id);
                    }
                  }}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-800"
                >
                  {expandedVote === vote._id ? 'Hide Results' : 'View Results'}
                </button>
                {expandedVote === vote._id && voteDetails && (
                  <div className="mt-3 pt-3 border-t">
                    <h5 className="font-medium text-sm mb-2">Vote Breakdown</h5>
                    {voteDetails.vote.options.map(opt => {
                      const count = voteDetails.optionCounts?.[opt.id] || 0;
                      const total = voteDetails.vote.results?.totalVotes || 1;
                      const percentage = Math.round((count / total) * 100);
                      return (
                        <div key={opt.id} className="mb-2">
                          <div className="flex justify-between text-sm">
                            <span>{opt.text}</span>
                            <span>{count} votes ({percentage}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-600 h-2 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    <div className="mt-2 text-xs text-gray-400">
                      Total votes: {voteDetails.vote.results?.totalVotes || 0}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      <button
                        onClick={() => {
                          const records = voteDetails.records || [];
                          if (records.length === 0) {
                            alert('No votes recorded yet');
                            return;
                          }
                          const breakdown = records.map(r => 
                            `${r.userId?.name}: ${voteDetails.vote.options.find(o => o.id === r.optionId)?.text || r.optionId}`
                          ).join('\n');
                          alert(`Votes:\n${breakdown}`);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Show who voted
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Closed Votes */}
      {closedVotes.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Recent Votes</h3>
          <div className="space-y-3">
            {closedVotes.slice(0, 10).map(vote => (
              <div key={vote._id} className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between">
                  <span className="font-medium">{vote.title}</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadge(vote.status)}`}>
                    {vote.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {vote.results?.totalVotes || 0} votes • {new Date(vote.createdAt).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {votes.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No votes yet. Propose the first one!
        </div>
      )}
    </div>
  );
}
